import {
  useHarnessCapabilities,
  useHarnessCatalog,
} from "@/api/harnessCapabilities";
import { defaultModelFor, offeredHarness } from "@/constants/harnesses";
import {
  HARNESS_IDS,
  HarnessCapability,
  HarnessCatalog,
  HarnessId,
} from "@cohesive/api-client";
import { useLocalStorageState } from "ahooks";
import { useCallback, useMemo } from "react";

export interface HarnessDraft {
  harness: HarnessId;
  /** Undefined on cohesive, which takes no model, and while the capabilities are still loading. */
  model?: string;
}

/**
 * What a surface's draft is stored as: the pick, stamped with the default it was made under
 * (`harnessDefaultStamp`). A draft whose stamp no longer matches the default was made for an
 * older default and stops applying; one without a stamp predates the preference, when the only
 * default was cohesive.
 */
interface StoredHarnessDraft extends HarnessDraft {
  seed?: string;
}

const DEFAULT_DRAFT: HarnessDraft = { harness: HARNESS_IDS.COHESIVE };

/**
 * Coerce a stored draft back onto what the user can actually start a chat on today. A harness
 * that has since lost its credential — or that the harness-runtime flag no longer offers — is
 * no longer selectable, and leaving it stored would send a create the server refuses. It falls
 * back to Cohesive as the catalog offers it.
 *
 * Unknown capabilities — still loading, or the request failed — fail closed to cohesive, the
 * one harness that needs no permission, because a create can fire before the answer lands and
 * an unverified harness is the one the server 403s. Read-only: the stored choice is untouched,
 * so it comes back intact the moment the catalog does. Nothing renders during that window
 * either — the selector waits on the same answer — so there is no cohesive to flash.
 */
export function sanitizeHarnessDraft(
  draft: HarnessDraft,
  capabilities: HarnessCapability[] | undefined,
): HarnessDraft {
  if (!capabilities) return DEFAULT_DRAFT;

  const offered = (harness: HarnessId) =>
    capabilities.find(
      (c) => c.id === offeredHarness(harness, capabilities) && c.available,
    );
  const own = offered(draft.harness);
  const capability = own ?? offered(DEFAULT_DRAFT.harness);
  if (!capability) return DEFAULT_DRAFT;
  const harness = capability.id as HarnessId;
  if (!capability.models.length) return { harness };

  // A fallback starts on Cohesive's own default rather than the lost harness's model, which
  // cohesive-claude-code may also list.
  const model =
    own && capability.models.some((m) => m.id === draft.model)
      ? draft.model
      : defaultModelFor(capability, !own);

  return { harness, model };
}

/**
 * The harness and model a new chat starts on when nothing picks one: the catalog's resolved
 * default — the user's preference where it still applies, else cohesive — with the model the
 * server would fill. Undefined until the catalog lands.
 */
export function harnessDefaultOf(
  catalog: HarnessCatalog | undefined,
): HarnessDraft | undefined {
  if (!catalog) return undefined;
  const capability = catalog.harness_capabilities.find(
    (c) => c.id === catalog.default_harness,
  );
  if (!capability?.available) return DEFAULT_DRAFT;
  return {
    harness: capability.id as HarnessId,
    model: defaultModelFor(capability, true),
  };
}

export function useHarnessDefault(): HarnessDraft | undefined {
  const { data: catalog } = useHarnessCatalog();
  return useMemo(() => harnessDefaultOf(catalog), [catalog]);
}

/**
 * The default as a draft is stamped with it; a draft that predates the stamp is cohesive's.
 * Cohesive on its catalog default model stamps as cohesive whichever loop serves it, so the
 * harness runtime moving Cohesive onto cohesive-claude-code is not a change of default.
 */
export function harnessDefaultStamp(
  draft: HarnessDraft,
  capabilities: HarnessCapability[] | undefined,
): string {
  if (
    draft.harness === HARNESS_IDS.COHESIVE_CLAUDE_CODE &&
    draft.model ===
      capabilities?.find((c) => c.id === draft.harness)?.default_model_id
  ) {
    return HARNESS_IDS.COHESIVE;
  }
  return draft.model ? `${draft.harness}/${draft.model}` : draft.harness;
}

const LEGACY_STAMP = HARNESS_IDS.COHESIVE;

/**
 * The draft a surface reads: its stored pick while that pick was made under the current
 * default, else the default itself. Exported for the seeding rule's tests; `useHarnessDraft`
 * is what surfaces call.
 */
export function seedHarnessDraft(
  stored: StoredHarnessDraft | undefined,
  harnessDefault: HarnessDraft | undefined,
  capabilities: HarnessCapability[] | undefined,
): HarnessDraft {
  if (!harnessDefault) return DEFAULT_DRAFT;
  const draft =
    stored &&
    (stored.seed ?? LEGACY_STAMP) ===
      harnessDefaultStamp(harnessDefault, capabilities)
      ? stored
      : harnessDefault;
  return sanitizeHarnessDraft(draft, capabilities);
}

/**
 * Persists a session-less composer's harness + model choice under `storageKey`, seeds it from
 * the user's default where none is stored, and coerces it back to Cohesive when what it names is
 * no longer selectable. Scope-agnostic, like `useReasoningEffort` next door, so each composer
 * surface keeps its own choice.
 *
 * A pick is this surface's alone, but only for as long as the default it was made under stands:
 * change the default and every surface re-seeds from it, the stored picks ignored rather than
 * swept. Draft state only: inside a chat the harness is stamped on the flow session and the
 * model is read from it, so neither is a local preference there.
 */
export function useHarnessDraft(storageKey: string) {
  const { data: capabilities } = useHarnessCapabilities();
  const harnessDefault = useHarnessDefault();
  // The choice is read by hooks other than the one the pill writes through — the canvas draft
  // has six live instances, and the new-chat buttons that spend it are never the composer that
  // set it. Without this each instance keeps the snapshot it mounted with and mints on a
  // harness the user has since changed, which the create then stamps for good.
  const [stored, setStored] = useLocalStorageState<StoredHarnessDraft>(
    storageKey,
    { listenStorageChange: true },
  );

  // Memoized because the sanitized draft is what the session-create callbacks depend on: a
  // fresh object each render would give every one of them a new identity, and they are props
  // on surfaces that rely on their stability.
  const draft = useMemo(
    () => seedHarnessDraft(stored, harnessDefault, capabilities),
    [stored, harnessDefault, capabilities],
  );

  const setDraft = useCallback(
    (next: HarnessDraft) =>
      setStored({
        ...next,
        seed: harnessDefaultStamp(
          harnessDefault ?? DEFAULT_DRAFT,
          capabilities,
        ),
      }),
    [setStored, harnessDefault, capabilities],
  );

  return [draft, setDraft] as const;
}

/**
 * The canvas's standing harness choice, keyed the way `useCanvasReasoningEffort` keys its tier.
 * Every user-started chat on a canvas reads it — the composer that offers the pill and the
 * new-chat buttons that mint a session before the user has typed anything — so a canvas answers
 * consistently however the chat was opened.
 */
export function useCanvasHarnessDraft(canvasId: string | undefined) {
  return useHarnessDraft(`canvas_harness_${canvasId ?? "__none__"}`);
}
